import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Unlock, Loader2, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loading, EmptyArt, Chip, cardCls, blueBtn } from "@/components/mobile/parts";
import { usePoll } from "@/hooks/useMobileData";
import { api, brl, errMsg, fmtTime } from "@/lib/api";
import { activeItems } from "@/lib/mobile";
import { cn } from "@/lib/utils";

const lastActivity = (c) => Math.max(new Date(c.opened_at).getTime(), ...c.items.map((i) => new Date(i.added_at || c.opened_at).getTime()));
function classify(c) {
  const empty = !activeItems(c).length;
  const paid = !empty && c.remaining <= 0;
  const hours = Math.floor((Date.now() - lastActivity(c)) / 3600000);
  return { empty, paid, hours, stuck: empty || paid || hours >= 3 };
}

function StuckCard({ c, info, onDone }) {
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const release = async () => {
    if (!window.confirm(info.empty ? `Liberar o número ${c.number}? A comanda vazia será encerrada.` : `Fechar a comanda #${c.number} (já paga) e liberar o número?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/comandas/${c.id}/release`);
      toast.success(data.mode === "closed" ? `Comanda fechada (venda #${data.sale_number}) e número ${c.number} liberado` : `Número ${c.number} liberado`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className={cn(cardCls, "flex items-center gap-3 p-3")} data-testid={`m-stuck-${c.number}`}>
      <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#1565c0] font-display text-lg font-bold text-white">{c.number}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{c.customer_name || (c.table_number ? `Mesa ${c.table_number}` : "Sem nome")}</p>
        <p className="text-xs text-slate-500">aberta {fmtTime(c.opened_at)} · {brl(c.remaining)} em aberto</p>
        <div className="mt-1 flex flex-wrap gap-1">
          {info.empty && <Chip tone="amber">Sem itens</Chip>}
          {info.paid && <Chip tone="green">Paga, não fechada</Chip>}
          {info.hours >= 3 && <Chip tone="red">Parada há {info.hours} h</Chip>}
        </div>
      </div>
      {info.empty || info.paid
        ? <Button className={cn("h-10", blueBtn)} disabled={busy} onClick={release} data-testid={`m-stuck-release-${c.number}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="mr-1 h-4 w-4" />}Liberar</Button>
        : <Button variant="outline" className="h-10" onClick={() => nav(`/m/comanda/${c.id}`)} data-testid={`m-stuck-open-${c.number}`}>Abrir</Button>}
    </div>
  );
}

export function StuckTab() {
  const { data, load } = usePoll(() => api.get("/comandas?status=open").then((r) => r.data), 15000);
  if (!data) return <Loading />;
  const list = data.map((c) => ({ c, info: classify(c) })).filter((x) => x.info.stuck).sort((a, b) => lastActivity(a.c) - lastActivity(b.c));
  return (
    <div className="space-y-2.5">
      <p className="text-xs text-slate-500">Comandas vazias, já pagas ou paradas há 3 horas ou mais. Libere o número para usar de novo.</p>
      {!list.length ? <EmptyArt icon={ClipboardCheck} title="Nenhuma comanda presa" testId="m-stuck-empty" /> : list.map(({ c, info }) => <StuckCard key={c.id} c={c} info={info} onDone={load} />)}
    </div>
  );
}
